// =============================================================
// FLOW (Wave 7a): Measurement TEMPLATES — the LL/L/H/HH schema editor, ported
// from scripts/perpetuum-desktop-sections5.jsx (measurement templates), now
// reading + WRITING the REAL /api/v1.
//
// Master/detail: the left card lists measurement templates (CRUD on the generic
// /measurement-templates collection); the right card edits the SELECTED
// template's POINTS via the Wave-7 point routes (/measurement-templates/:id/
// points). Each point carries a name, optional unit and four thresholds
// (LL ≤ L ≤ H ≤ HH); a ThresholdBar preview renders the bands using the SAME
// core tolerance rule the server + Device-Card use. Writes are gated by
// dev:write; deletes by dev:delete. The whole screen is gated by the
// `measurements` feature flag (nav entry hidden when off; see modules.ts +
// Shell). No synthetic data — empty templates/points show EmptyState.
// =============================================================

import { useEffect, useMemo, useState } from "react";
import { useCollection, useCreateRow, useUpdateRow, useDeleteRow, type Row } from "../lib/queries.js";
import {
  useMeasurementPoints,
  useAddMeasurementPoint,
  useUpdateMeasurementPoint,
  useDeleteMeasurementPoint,
} from "../lib/queries.js";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS, ThresholdBar } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";
import { Modal } from "../components/Modal.js";
import type { MeasurementPointRecord } from "@perpetuum/core";

interface TemplateRow extends Row {
  categoryId?: string | null;
}

/** A blank point form (sensible default ordered thresholds). */
const BLANK_POINT = { name: "", unit: "", ll: "0", l: "1", h: "9", hh: "10" };

export function MeasurementTemplates() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast, confirm } = useChrome();

  const templatesQ = useCollection<TemplateRow>("measurement-templates");
  const categoriesQ = useCollection<Row>("categories");
  const createTpl = useCreateRow("measurement-templates");
  const updateTpl = useUpdateRow("measurement-templates");
  const deleteTpl = useDeleteRow("measurement-templates");

  const templates = templatesQ.data ?? [];
  const categories = categoriesQ.data ?? [];
  const catById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const canWrite = can("dev", "write");
  const canDelete = can("dev", "delete");

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Keep a valid selection as the list loads / changes.
  useEffect(() => {
    if (templates.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !templates.some((tp) => tp.id === selectedId)) {
      setSelectedId(templates[0]!.id);
    }
  }, [templates, selectedId]);

  // Template create/edit modal.
  const [tplModal, setTplModal] = useState<{ id: string | null } | null>(null);
  const [tplName, setTplName] = useState("");
  const [tplCat, setTplCat] = useState("");

  const openTplCreate = () => {
    setTplName("");
    setTplCat("");
    setTplModal({ id: null });
  };
  const openTplEdit = (tp: TemplateRow) => {
    setTplName(String(tp.name ?? ""));
    setTplCat(tp.categoryId ?? "");
    setTplModal({ id: tp.id });
  };
  const closeTpl = () => setTplModal(null);

  const onSubmitTpl = async () => {
    const name = tplName.trim();
    if (!name) return;
    const body = { name, categoryId: tplCat || null };
    try {
      if (tplModal?.id) {
        await updateTpl.mutateAsync({ id: tplModal.id, body });
        toast(t("settings.dict.updated", { name }), "success");
      } else {
        const created = await createTpl.mutateAsync(body);
        toast(t("settings.dict.added", { name, group: t("measTpl.list") }), "success");
        if (created?.id) setSelectedId(created.id);
      }
      closeTpl();
    } catch {
      toast(t("settings.dict.writeErr"), "error");
    }
  };

  const onDeleteTpl = async (tp: TemplateRow) => {
    const ok = await confirm({
      title: t("measTpl.deleteTitle"),
      body: t("measTpl.deleteBody"),
      confirmLabel: t("settings.dict.delete"),
    });
    if (!ok) return;
    try {
      await deleteTpl.mutateAsync(tp.id);
      toast(t("settings.dict.deleted", { name: String(tp.name ?? "") }), "info");
      if (selectedId === tp.id) setSelectedId(null);
    } catch {
      toast(t("settings.dict.deleteErr"), "error");
    }
  };

  const selected = templates.find((tp) => tp.id === selectedId) ?? null;

  return (
    <div>
      <h2 className="d-h2">{t("measTpl.title")}</h2>
      <p className="d-sub">{t("measTpl.sub")}</p>

      <div className="d-grid-2" style={{ gridTemplateColumns: "1fr 1.6fr", alignItems: "start" }}>
        {/* ---- Templates list ---- */}
        <div className="d-card">
          <div className="d-card-hd">
            <h3>{t("measTpl.list")}</h3>
            <span className="sp" style={{ flex: 1 }} />
            {!templatesQ.isLoading && (
              <span className="ro-badge" data-testid="tpl-count">
                {templates.length}
              </span>
            )}
            {canWrite && (
              <Btn variant="primary" size="sm" data-testid="tpl-add-btn" onClick={openTplCreate}>
                <Icon d={ICONS.plus} size={15} />
                {t("measTpl.add")}
              </Btn>
            )}
          </div>
          <div className="d-card-body" style={{ padding: 0 }}>
            {templatesQ.isLoading ? (
              <div style={{ padding: 12 }}>
                <Skeleton variant="text" count={4} />
              </div>
            ) : templates.length === 0 ? (
              <div style={{ padding: 8 }}>
                <EmptyState
                  icon={ICONS.gauge}
                  title={t("measTpl.empty")}
                  hint={t("measTpl.emptyHint")}
                  action={canWrite ? { label: t("measTpl.add"), onClick: openTplCreate } : undefined}
                />
              </div>
            ) : (
              <table className="d-table" data-testid="tpl-table">
                <tbody>
                  {templates.map((tp) => {
                    const cat = tp.categoryId ? catById.get(tp.categoryId) : undefined;
                    const active = tp.id === selectedId;
                    return (
                      <tr
                        key={tp.id}
                        className="rowhover"
                        aria-selected={active}
                        data-testid={`tpl-row-${tp.id}`}
                        style={active ? { background: "var(--brand-wash)" } : undefined}
                      >
                        <td>
                          <button
                            className="linklike"
                            style={{ fontWeight: 600, background: "none", border: 0, cursor: "pointer", color: "inherit", textAlign: "left" }}
                            onClick={() => setSelectedId(tp.id)}
                            data-testid={`tpl-select-${tp.id}`}
                          >
                            {String(tp.name ?? "—")}
                          </button>
                          <div className="dt-note">{cat ? String(cat.name) : t("measTpl.categoryNone")}</div>
                        </td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          {canWrite && (
                            <button
                              className="row-icon-btn"
                              title={t("measTpl.rename")}
                              aria-label={t("settings.dict.editNamed", { name: String(tp.name ?? "") })}
                              data-testid={`tpl-edit-${tp.id}`}
                              onClick={() => openTplEdit(tp)}
                            >
                              <Icon d={ICONS.cog} size={15} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="row-icon-btn"
                              title={t("settings.dict.delete")}
                              aria-label={t("settings.dict.deleteNamed", { name: String(tp.name ?? "") })}
                              data-testid={`tpl-delete-${tp.id}`}
                              onClick={() => void onDeleteTpl(tp)}
                            >
                              <Icon d={ICONS.trash} size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ---- Points editor for the selected template ---- */}
        {selected ? (
          <PointsEditor
            key={selected.id}
            templateId={selected.id}
            templateName={String(selected.name ?? "")}
            canWrite={canWrite}
            canDelete={canDelete}
          />
        ) : (
          <div className="d-card">
            <div className="d-card-body">
              <EmptyState compact icon={ICONS.gauge} title={t("measTpl.selectHint")} />
            </div>
          </div>
        )}
      </div>

      {tplModal && (
        <Modal
          title={tplModal.id ? t("measTpl.rename") : t("measTpl.addTitle")}
          icon={ICONS.gauge}
          onClose={closeTpl}
          testId="tpl-dialog"
          width={440}
        >
          <div className="modal-body">
            <div className="d-field">
              <label htmlFor="tpl-name">{t("measTpl.name")}</label>
              <div className="field-ctrl">
                <input
                  id="tpl-name"
                  data-testid="tpl-name-input"
                  value={tplName}
                  placeholder={t("measTpl.namePlaceholder")}
                  onChange={(e) => setTplName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void onSubmitTpl();
                  }}
                />
              </div>
            </div>
            <div className="d-field">
              <label htmlFor="tpl-cat">{t("measTpl.category")}</label>
              <div className="field-ctrl">
                <select id="tpl-cat" data-testid="tpl-cat-input" value={tplCat} onChange={(e) => setTplCat(e.target.value)}>
                  <option value="">{t("measTpl.categoryNone")}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {String(c.name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <Btn variant="ghost" size="sm" onClick={closeTpl}>
              {t("common.cancel")}
            </Btn>
            <Btn
              variant="primary"
              size="sm"
              data-testid="tpl-save"
              disabled={!tplName.trim() || createTpl.isPending || updateTpl.isPending}
              onClick={() => void onSubmitTpl()}
            >
              {t("settings.dict.save")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** A numeric string → number, or NaN. */
const num = (s: string) => parseFloat(String(s).replace(",", "."));

function PointsEditor({
  templateId,
  templateName,
  canWrite,
  canDelete,
}: {
  templateId: string;
  templateName: string;
  canWrite: boolean;
  canDelete: boolean;
}) {
  const t = useScopedT();
  const { toast, confirm } = useChrome();
  const pointsQ = useMeasurementPoints(templateId);
  const addPoint = useAddMeasurementPoint();
  const updatePoint = useUpdateMeasurementPoint();
  const deletePoint = useDeleteMeasurementPoint();

  const points = pointsQ.data ?? [];

  const [modal, setModal] = useState<{ id: string | null } | null>(null);
  const [form, setForm] = useState({ ...BLANK_POINT });

  const openCreate = () => {
    setForm({ ...BLANK_POINT });
    setModal({ id: null });
  };
  const openEdit = (p: MeasurementPointRecord) => {
    setForm({
      name: p.name,
      unit: p.unit ?? "",
      ll: String(p.ll),
      l: String(p.l),
      h: String(p.h),
      hh: String(p.hh),
    });
    setModal({ id: p.id });
  };
  const close = () => setModal(null);

  // Live-parse the form thresholds for the preview + validation.
  const parsed = {
    ll: num(form.ll),
    l: num(form.l),
    h: num(form.h),
    hh: num(form.hh),
  };
  const allFinite = [parsed.ll, parsed.l, parsed.h, parsed.hh].every(Number.isFinite);
  const ordered = allFinite && parsed.ll <= parsed.l && parsed.l <= parsed.h && parsed.h <= parsed.hh;
  const canSubmit = form.name.trim().length > 0 && ordered && !addPoint.isPending && !updatePoint.isPending;

  const onSubmit = async () => {
    if (!canSubmit) return;
    const body = {
      name: form.name.trim(),
      unit: form.unit.trim() || null,
      ll: parsed.ll,
      l: parsed.l,
      h: parsed.h,
      hh: parsed.hh,
    };
    try {
      if (modal?.id) {
        await updatePoint.mutateAsync({ templateId, pointId: modal.id, body });
        toast(t("settings.dict.updated", { name: body.name }), "success");
      } else {
        await addPoint.mutateAsync({ templateId, ...body });
        toast(t("settings.dict.added", { name: body.name, group: t("measTpl.points") }), "success");
      }
      close();
    } catch {
      toast(t("settings.dict.writeErr"), "error");
    }
  };

  const onDelete = async (p: MeasurementPointRecord) => {
    const ok = await confirm({
      title: t("measTpl.pointDeleteTitle"),
      body: t("measTpl.pointDeleteBody"),
      confirmLabel: t("settings.dict.delete"),
    });
    if (!ok) return;
    try {
      await deletePoint.mutateAsync({ templateId, pointId: p.id });
      toast(t("settings.dict.deleted", { name: p.name }), "info");
    } catch {
      toast(t("settings.dict.deleteErr"), "error");
    }
  };

  return (
    <div className="d-card" data-testid="points-editor">
      <div className="d-card-hd">
        <h3>{t("measTpl.points")}</h3>
        <span className="dict-hint">{templateName}</span>
        <span className="sp" style={{ flex: 1 }} />
        {!pointsQ.isLoading && (
          <span className="ro-badge" data-testid="points-count">
            {points.length}
          </span>
        )}
        {canWrite && (
          <Btn variant="primary" size="sm" data-testid="point-add-btn" onClick={openCreate}>
            <Icon d={ICONS.plus} size={15} />
            {t("measTpl.pointAdd")}
          </Btn>
        )}
      </div>
      <div className="d-card-body">
        {pointsQ.isLoading ? (
          <Skeleton variant="card" height={120} count={2} />
        ) : points.length === 0 ? (
          <EmptyState
            icon={ICONS.gauge}
            title={t("measTpl.pointsEmpty")}
            hint={t("measTpl.pointsEmptyHint")}
            action={canWrite ? { label: t("measTpl.pointAdd"), onClick: openCreate } : undefined}
          />
        ) : (
          points.map((p) => (
            <div key={p.id} className="d-card" style={{ marginBottom: 12 }} data-testid={`point-row-${p.id}`}>
              <div className="d-card-body">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <strong style={{ flex: 1 }} data-testid="point-name">
                    {p.name}
                    {p.unit ? <small style={{ color: "var(--sys-fg-3)", marginLeft: 6 }}>({p.unit})</small> : null}
                  </strong>
                  {canWrite && (
                    <button className="row-icon-btn" title={t("measTpl.pointEdit")} aria-label={t("settings.dict.editNamed", { name: p.name })} data-testid={`point-edit-${p.id}`} onClick={() => openEdit(p)}>
                      <Icon d={ICONS.cog} size={15} />
                    </button>
                  )}
                  {canDelete && (
                    <button className="row-icon-btn" title={t("settings.dict.delete")} aria-label={t("settings.dict.deleteNamed", { name: p.name })} data-testid={`point-delete-${p.id}`} onClick={() => void onDelete(p)}>
                      <Icon d={ICONS.trash} size={15} />
                    </button>
                  )}
                </div>
                {/* Preview the bands with the marker parked at the centre of the normal zone. */}
                <ThresholdBar
                  p={{ name: t("measTpl.preview"), unit: p.unit ?? undefined, ll: p.ll, l: p.l, h: p.h, hh: p.hh, value: (p.l + p.h) / 2 }}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {modal && (
        <Modal
          title={modal.id ? t("measTpl.pointEdit") : t("measTpl.pointAdd")}
          icon={ICONS.gauge}
          onClose={close}
          testId="point-dialog"
          width={460}
        >
          <div className="modal-body">
            <div className="d-field">
              <label htmlFor="pt-name">{t("measTpl.pointName")}</label>
              <div className="field-ctrl">
                <input
                  id="pt-name"
                  data-testid="point-name-input"
                  value={form.name}
                  placeholder={t("measTpl.pointNamePlaceholder")}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
            </div>
            <div className="d-field">
              <label htmlFor="pt-unit">{t("measTpl.unit")}</label>
              <div className="field-ctrl">
                <input
                  id="pt-unit"
                  data-testid="point-unit-input"
                  value={form.unit}
                  placeholder={t("measTpl.unitPlaceholder")}
                  onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                />
              </div>
            </div>
            <div className="d-grid-2" style={{ gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {(["ll", "l", "h", "hh"] as const).map((k) => (
                <div className="d-field" key={k}>
                  <label htmlFor={`pt-${k}`}>{k.toUpperCase()}</label>
                  <div className="field-ctrl">
                    <input
                      id={`pt-${k}`}
                      data-testid={`point-${k}-input`}
                      type="number"
                      step="any"
                      value={form[k]}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                    />
                  </div>
                </div>
              ))}
            </div>
            {!ordered && allFinite && (
              <div className="inline-alert warn" data-testid="point-order-warn" style={{ marginTop: 8 }}>
                {t("measTpl.thresholdOrder")}
              </div>
            )}
            {/* Live band preview as the user edits. */}
            {ordered && (
              <div style={{ marginTop: 12 }}>
                <ThresholdBar
                  p={{ name: t("measTpl.preview"), unit: form.unit || undefined, ll: parsed.ll, l: parsed.l, h: parsed.h, hh: parsed.hh, value: (parsed.l + parsed.h) / 2 }}
                />
              </div>
            )}
          </div>
          <div className="modal-foot">
            <Btn variant="ghost" size="sm" onClick={close}>
              {t("common.cancel")}
            </Btn>
            <Btn variant="primary" size="sm" data-testid="point-save" disabled={!canSubmit} onClick={() => void onSubmit()}>
              {t("settings.dict.save")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
